import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import AdminWarningList from '../../components/admin/AdminWarningList';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';
import { adminShortJson } from '../../admin/adminHubPresentation';

function flattenGuestLimits(limits) {
  if (!limits) return [];
  const q = limits.quotasFromCatalog || {};
  const l = limits.limitsFromCatalog || {};
  const keys = new Set([...Object.keys(q), ...Object.keys(l)]);
  return Array.from(keys).map((k) => ({
    __rowKey: k,
    resource: k,
    quota: q[k] != null ? String(q[k]) : '—',
    limit: l[k] != null ? String(l[k]) : '—',
  }));
}

function capabilityRows(policyDoc) {
  const caps = policyDoc?.capabilities;
  if (!caps || typeof caps !== 'object') return [];
  return Object.keys(caps).map((k) => {
    const c = caps[k];
    return {
      __rowKey: k,
      capability: k,
      allowed: !!c?.allowed,
      note: c?.note || '—',
    };
  });
}

export default function AdminCommercialGuestPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_COMMERCIAL_GUEST_POLICY), {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('bad');
        const json = await res.json();
        if (!cancel) {
          setData(json);
          setErr(false);
        }
      } catch {
        if (!cancel) setErr(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const policyRows = useMemo(() => {
    const policy = data?.limits?.policy || {};
    return [
      {
        __rowKey: 'chat',
        area: t('adminKit.guestAreaChat'),
        allowed: policy.chat_limited ? t('adminKit.guestLimited') : t('adminKit.guestYes'),
        detail: t('adminKit.guestChatDetail'),
      },
      {
        __rowKey: 'doc',
        area: t('adminKit.guestAreaDocuments'),
        allowed: policy.document_upload_allowed ? t('adminKit.yes') : t('adminKit.no'),
        detail: t('adminKit.guestDocDetail'),
      },
      {
        __rowKey: 'firm',
        area: t('adminKit.guestAreaFirm'),
        allowed: policy.firm_mode_allowed ? t('adminKit.yes') : t('adminKit.no'),
        detail: t('adminKit.guestFirmDetail'),
      },
      {
        __rowKey: 'arsheef',
        area: t('adminKit.guestAreaArsheef'),
        allowed: policy.arsheef_allowed ? t('adminKit.yes') : t('adminKit.no'),
        detail: t('adminKit.guestArsheefDetail'),
      },
      {
        __rowKey: 'lie',
        area: t('adminKit.guestAreaHeavyLie'),
        allowed: policy.heavy_lie_analysis_allowed ? t('adminKit.yes') : t('adminKit.no'),
        detail: t('adminKit.guestLieDetail'),
      },
    ];
  }, [data, t]);

  const capRows = useMemo(() => {
    const rows = capabilityRows(data?.policyDoc).map((r) => ({
      ...r,
      allowedDisplay: (
        <AdminStatusBadge tone={r.allowed ? 'ok' : 'warn'}>{r.allowed ? t('adminKit.yes') : t('adminKit.no')}</AdminStatusBadge>
      ),
    }));
    return rows;
  }, [data, t]);

  const limitRows = useMemo(() => flattenGuestLimits(data?.limits), [data]);

  const softSample = data?.softWarningSample;
  const softList = useMemo(() => {
    if (!softSample?.warn) return [];
    return [
      {
        code: softSample.code || 'guest_soft',
        message: softSample.message || t('adminKit.guestSoftGeneric'),
        tone: 'soft',
      },
    ];
  }, [softSample, t]);

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navCommercial'), to: '/admin/commercial/plans' },
      { label: t('adminHub.navGuestPolicies') },
    ],
    [t]
  );

  const summaryCols = useMemo(
    () => [
      { key: 'area', header: t('adminKit.colArea') },
      { key: 'allowed', header: t('adminKit.colAllowed') },
      { key: 'detail', header: t('adminKit.colDetail') },
    ],
    [t]
  );

  const capCols = useMemo(
    () => [
      { key: 'capability', header: t('adminKit.colCapability') },
      { key: 'allowedDisplay', header: t('adminKit.colAllowed') },
      { key: 'note', header: t('adminKit.colNote') },
    ],
    [t]
  );

  const limCols = useMemo(
    () => [
      { key: 'resource', header: t('adminKit.colResource') },
      { key: 'quota', header: t('adminKit.colQuotaCatalog') },
      { key: 'limit', header: t('adminKit.colLimitCatalog') },
    ],
    [t]
  );

  return (
    <AdminHubPageFrame
      titleKey="adminHub.guestPoliciesTitle"
      descKey="adminHub.guestPoliciesDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : !data ? (
          <div className="admin-state admin-state--loading" role="status">
            <p>{t('adminPage.loading')}</p>
          </div>
        ) : (
          <div className="admin-hub-kit-stack">
            <AdminWarningList items={softList} />
            <AdminSectionHeader title={t('adminKit.guestSummaryTitle')} subtitle={t('adminKit.guestSummarySub')} />
            <AdminDataTable columns={summaryCols} rows={policyRows} emptyMessage={t('adminKit.tableEmpty')} />

            <AdminSectionHeader title={t('adminKit.guestPolicyDocTitle')} level={3} />
            <AdminDataTable columns={capCols} rows={capRows} emptyMessage={t('adminKit.tableEmpty')} />

            <AdminSectionHeader title={t('adminKit.guestCatalogLimitsTitle')} level={3} />
            <AdminDataTable columns={limCols} rows={limitRows} emptyMessage={t('adminKit.tableEmpty')} />

            <p className="admin-hub-kit-note">
              {t('adminKit.guestEnforcementNote')} {adminShortJson({ enforcement: data.limits?.enforcement, source: data.limits?.source }, 200)}
            </p>

            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        )}
      </section>
    </AdminHubPageFrame>
  );
}
